import {Button, Popover, Skeleton} from 'antd'
import {course_class_getGradeDetailsByAuth_Api, course_class_getGrades_Api, course_getGradeReport_Api,} from 'apis'
import {getId} from 'apis/model/base'
import {LoginContext} from 'components/LoginContext'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {BiChevronDown, BiChevronUp, BiSearch, BiSolidEdit, BiTime, BiTrash} from 'react-icons/bi'
import {RiSearchEyeLine} from 'react-icons/ri'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {NullAsDefault} from 'views/Shared'
import {renderCourseClassGrade} from 'views/Workspace/Course/functions/renderCourseClassGrade'
import {GradeActionTypes} from '../..'
import getTitle from '../../../../../helpers/getTitle'
import EntityList from '../../../EntityList'
import {bindQueryParam, bindQueryParams} from '../../../functions/routerHelper'
import CourseClassContext from '../../CourseClassContext'
import CouseClassGradeFilter from "../../../../Workspace/Course/components/filters/CouseClassGradeFilter";
import usePaginationHelper, {createPagerKey} from "../../../hooks/usePaginationHelper";
import {DateNameSortBy} from "../../../factory/createEntitySortBy";
import {getType} from "../../../../../apis/model/base";
import EmptyHolder from "../../../../../components/EmptyHolder";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";
import {BsPencilSquare, BsThreeDots} from "react-icons/bs";
import getLinkToDetail from "../../../../../helpers/getLinkToDetail";
import {Link} from "react-router-dom";
import history, {useRouteSearchParam} from "../../../../../envs/_current/history";
import {PiChartBarFill, PiUserListFill} from "react-icons/pi";
import {TbClockEdit} from "react-icons/tb";
import {useCourseGrades} from "../../functions/useCourseGrades";
import classNames from "classnames";
import dayjs from "dayjs";
import {CourseClassTabHeader} from "../components/CourseClassTabHeader";

const AVATAR_COLORS = ['bg-indigo-500', 'bg-green-500', 'bg-red-400', 'bg-yellow-500', 'bg-purple-500', 'bg-blue-500', 'bg-pink-500', 'bg-teal-500']
const getAvatarColor = (str = '') => {
    let hash = 0
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const GRADE_COLS_THRESHOLD = 3

export const GradeStudentReport = ({course_class_id, activeSubjectId, passedScore = 0}) => {
    const t = useTranslate()
    const gradeColumnsRef = useRef([])
    const [expandedRows, setExpandedRows] = useState(new Set())
    const [keyword, setKeyword] = useState('')
    const [statusFilter, setStatusFilter] = useState(null)

    React.useEffect(() => {
        setKeyword('')
        setStatusFilter(null)
        setExpandedRows(new Set())
        gradeColumnsRef.current = []
    }, [activeSubjectId])

    const toggleExpand = useCallback((index) => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            next.has(index) ? next.delete(index) : next.add(index)
            return next
        })
    }, [])

    const statusFilters = useMemo(() => [
        {value: null, label: t('all'), color: null},
        {value: 1, label: t('passed'), color: 'green'},
        {value: 0, label: t('failed'), color: 'red'},
        {value: -1, label: t('not yet graded'), color: 'gray'},
    ], [t])

    const renderRow = useCallback((student, index) => {
        const {student_name, student_idcode, score_number, result_status, grade_data: grades = []} = student ?? {}
        const name = student_name || '-'

        if (keyword) {
            const kw = keyword.toLowerCase()
            if (!name.toLowerCase().includes(kw) && !(student_idcode || '').toLowerCase().includes(kw)) return null
        }
        if (statusFilter === 1 && result_status !== 1) return null
        if (statusFilter === 0 && result_status !== 0) return null
        if (statusFilter === -1 && result_status != null) return null

        const initial = name.charAt(0).toUpperCase()
        const isExpanded = expandedRows.has(index)

        if (index === 0 && grades.length > 0) {
            gradeColumnsRef.current = grades.map(g => ({id: g.grade_id, title: g.title}))
        }

        const colDefs = gradeColumnsRef.current
        const numCols = colDefs.length || grades.length
        const hasMany = numCols > GRADE_COLS_THRESHOLD

        let result_label = t('not yet graded'), result_cls = 'text-color-400'
        if (result_status === 1) { result_label = t('passed'); result_cls = 'bg-green-50 text-green-600' }
        else if (result_status === 0) { result_label = t('failed'); result_cls = 'bg-red-50 text-red-500' }

        let score_color = 'text-color-300'
        if (score_number != null) {
            if (passedScore > 0) {
                score_color = score_number >= passedScore ? 'text-green-600' : 'text-red-500'
            } else {
                if (score_number >= 8) score_color = 'text-green-600'
                else if (score_number >= 5) score_color = 'text-yellow-500'
                else score_color = 'text-red-500'
            }
        }

        const avatarCell = (
            <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                    <div className={classNames('w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0', getAvatarColor(name))}>
                        {initial}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 truncate max-w-[110px]">{name}</span>
                </div>
                {student_idcode && (
                    <span className="text-2xs text-color-400 ml-7 font-mono">{student_idcode}</span>
                )}
            </div>
        )
        const scoreVal = <span className={classNames('text-sm font-bold tabular-nums', score_color)}>{score_number ?? '-'}</span>
        const resultBadge = (
            <span className={classNames('text-xs font-semibold px-2 py-0.5 rounded-full whitespace-no-wrap', result_cls)}>
                {result_label}
            </span>
        )

        if (!hasMany) {
            return (
                <tr key={index} className="border-b border-color-50 hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2">{avatarCell}</td>
                    {grades.length > 0
                        ? grades.map((g, gi) => (
                            <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap">
                                <span className="text-sm text-color-200">{g.score_number ?? '-'}</span>
                            </td>
                        ))
                        : colDefs.map((_, gi) => (
                            <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap">
                                <span className="text-sm text-color-400">-</span>
                            </td>
                        ))
                    }
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{scoreVal}</td>
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{resultBadge}</td>
                </tr>
            )
        }

        const popoverContent = (
            <div className="flex flex-col gap-2 min-w-[160px]">
                {grades.map(g => (
                    <div key={g.grade_id} className="flex justify-between gap-4 text-xs">
                        <span className="text-color-400 truncate">{g.title}</span>
                        <span className="font-semibold text-color-100 flex-shrink-0">{g.score_number ?? '-'}</span>
                    </div>
                ))}
            </div>
        )

        return (
            <React.Fragment key={index}>
                <tr className="border-b border-color-50 hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2">{avatarCell}</td>
                    {(grades.length > 0 ? grades : colDefs).slice(0, 2).map((g, gi) => (
                        <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap">
                            <span className="text-sm text-color-200">{grades[gi]?.score_number ?? '-'}</span>
                        </td>
                    ))}
                    <td className="px-3 py-2 text-center whitespace-no-wrap">
                        <Popover
                            content={popoverContent}
                            title={<span className="text-xs font-semibold">{t('all grades')}</span>}
                            trigger="hover"
                            placement="top">
                            <span className="inline-flex items-center text-xs px-2 py-1 bg-indigo-50 text-indigo-500 rounded-full cursor-pointer hover:bg-indigo-100 transition-colors whitespace-no-wrap">
                                +{numCols - 2}
                            </span>
                        </Popover>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{scoreVal}</td>
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{resultBadge}</td>
                    <td className="px-2 py-2 text-center whitespace-no-wrap">
                        <button
                            className="p-1 rounded hover:bg-gray-100 text-color-300 hover:text-color-100 transition-colors"
                            onClick={() => toggleExpand(index)}>
                            {isExpanded ? <BiChevronUp size={16}/> : <BiChevronDown size={16}/>}
                        </button>
                    </td>
                </tr>
                {isExpanded && (
                    <tr className="bg-indigo-50/20 border-b border-color-50">
                        <td className="px-3 py-2"/>
                        <td colSpan={5} className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                                {grades.map(g => (
                                    <div key={g.grade_id} className="flex flex-col items-center px-3 py-2 bg-white rounded-lg border border-color-100 min-w-[80px]">
                                        <span className="text-xs text-color-400 truncate max-w-[100px] text-center leading-tight">{g.title}</span>
                                        <span className="text-sm font-bold text-color-100 mt-0.5">{g.score_number ?? '-'}</span>
                                    </div>
                                ))}
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        )
    }, [t, keyword, statusFilter, expandedRows, toggleExpand, passedScore])

    const renderTable = useCallback((child) => {
        const gradeCols = gradeColumnsRef.current
        const hasMany = gradeCols.length > GRADE_COLS_THRESHOLD
        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="background-100 border-b border-color-100">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-color-300 uppercase tracking-wider">{t('student')}</th>
                            {!hasMany
                                ? gradeCols.map(col => (
                                    <th key={col.id} className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider">{col.title}</th>
                                ))
                                : (
                                    <>
                                        {gradeCols.slice(0, 2).map(col => (
                                            <th key={col.id} className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider">{col.title}</th>
                                        ))}
                                        <th className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">{t('grades')}</th>
                                    </>
                                )
                            }
                            <th className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">{t('score')}</th>
                            <th className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">{t('outcome')}</th>
                            {hasMany && <th className="px-2 py-2 w-px"/>}
                        </tr>
                    </thead>
                    <tbody className="background divide-y divide-color-50">{child}</tbody>
                </table>
            </div>
        )
    }, [t])

    if (!activeSubjectId) return <EmptyHolder title={null}/>

    return (
        <div className="flex flex-col gap-3">
            {/* Status filter + threshold */}
            <div className="flex flex-wrap gap-1.5">
                {statusFilters.map((f, i) => {
                    const isActive = statusFilter === f.value
                    const cls = isActive
                        ? f.color === 'green' ? 'bg-green-600 border-green-600 text-white'
                            : f.color === 'red' ? 'bg-red-500 border-red-500 text-white'
                                : f.color === 'gray' ? 'bg-color-300 border-color-300 text-white'
                                    : 'bg-indigo-600 border-indigo-600 text-white'
                        : f.color === 'green' ? 'border-color-200 text-green-600 hover:border-green-500'
                            : f.color === 'red' ? 'border-color-200 text-red-500 hover:border-red-400'
                                : 'border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                    return (
                        <button
                            key={i}
                            onClick={() => setStatusFilter(isActive ? null : f.value)}
                            className={classNames('px-3 py-1 text-xs font-medium rounded-full border transition-colors select-none', cls)}>
                            {f.label}
                        </button>
                    )
                })}
                {passedScore > 0 && (
                    <span className="text-xs text-green-600 font-medium italic self-center">
                        {`* ${t('passed score')} : ≥${passedScore}`}
                    </span>
                )}
            </div>

            {/* Search */}
            <div className="relative">
                <BiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-color-400 pointer-events-none"/>
                <input
                    type="text"
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    placeholder={t('search by name or student code')}
                    className="w-full pl-7 pr-6 py-2 text-xs border border-color-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 background text-color-100 placeholder-color-400 transition-colors"
                />
                {keyword && (
                    <button
                        onClick={() => setKeyword('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-xs transition-colors">
                        ×
                    </button>
                )}
            </div>

            <EntityList
                key={[course_class_id, activeSubjectId].join('-')}
                refreshKey={[course_class_id, activeSubjectId].join('-')}
                RenderEntity={React.Fragment}
                renderEmpty={() => <EmptyHolder title={null}/>}
                renderItem={renderRow}
                renderWrap={renderTable}
                renderLoading={() => (
                    <>
                        {[...Array(5)].map((_, i) => (
                            <tr key={i} className="border-b border-color-50">
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <Skeleton.Avatar active size={28}/>
                                        <Skeleton.Input active size="small" style={{width: 100, height: 14}}/>
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-center"><Skeleton.Input active size="small" style={{width: 40, height: 14}}/></td>
                                <td className="px-3 py-2 text-center"><Skeleton.Input active size="small" style={{width: 50, height: 14}}/></td>
                            </tr>
                        ))}
                    </>
                )}
                query={bindQueryParams([{id: course_class_id}, {subject_id: activeSubjectId}])}
                apiInfo={course_getGradeReport_Api}
            />
        </div>
    )
}

const MyBoard = ({course_class_id}) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const [items, setItems] = useState([])
    const [myRelationship, setMyRelationship] = useState(null)
    const [loading, setLoading] = useState(false)

    const isSelf = !myRelationship || myRelationship === 'SELF'

    const {handleAsyncAction: loadGrades} = useAsyncAction({
        apiInfo: course_class_getGradeDetailsByAuth_Api,
        query: {id: course_class_id},
        onSuccess: (result, {response}) => {
            const resp = (response && response.data) || {}
            setItems(resp.data || [])
            setMyRelationship(resp.my_relationship || null)
            setLoading(false)
        },
        onError: () => setLoading(false),
    })

    useEffect(() => {
        if (!login || !course_class_id) return
        setLoading(true)
        loadGrades()
    }, [course_class_id])

    const activeExamItems = useMemo(
        () => isSelf
            ? items.filter(item =>
                item.grade &&
                item.grade.course_class_exam &&
                item.grade.course_class_exam.status === 1
            )
            : [],
        [items, isSelf]
    )

    if (!login) return null

    if (loading) {
        return (
            <div className="flex flex-col gap-2">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border-l-4 border-gray-100 rounded-r-xl bg-white">
                        <Skeleton.Avatar active size={40} shape="square"/>
                        <div className="flex-1">
                            <Skeleton.Input active size="small" style={{width: 160, height: 14}}/>
                            <div className="mt-1"><Skeleton.Input active size="small" style={{width: 100, height: 12}}/></div>
                        </div>
                        <Skeleton.Input active size="small" style={{width: 60, height: 26}}/>
                    </div>
                ))}
            </div>
        )
    }

    if (!items.length) return <EmptyHolder title={null}/>

    return (
        <div className="flex flex-col gap-2">
            {!isSelf && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                    <span className="text-xs text-blue-500 font-medium">
                        {t('Viewing as parent')}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-semibold">
                        {t(myRelationship)}
                    </span>
                </div>
            )}
            {activeExamItems.length > 0 && (
                <div className="flex flex-col gap-2 mb-1">
                    {activeExamItems.map((examItem, i) => {
                        const exam = examItem.grade.course_class_exam
                        const gradeTitle = getTitle(examItem.grade)
                        return (
                            <div
                                key={i}
                                onClick={() => history.push('/course-class-exam/' + exam.id)}
                                className="cursor-pointer flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-md hover:shadow-lg active:scale-[0.99] transition-all select-none">
                                <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-white/20">
                                    <BsPencilSquare size={18}/>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-2xs text-indigo-200 font-medium uppercase tracking-wide leading-none mb-1">{t('exam available')}</p>
                                    <p className="font-bold text-sm leading-tight truncate">{gradeTitle}</p>
                                    {exam.title && exam.title !== gradeTitle && (
                                        <p className="text-xs text-indigo-100 truncate mt-0.5">{exam.title}</p>
                                    )}
                                    <div className="flex items-center gap-3 mt-1.5">
                                        {exam.duration_minutes > 0 && (
                                            <span className="flex items-center gap-1 text-xs text-indigo-200">
                                                <BiTime size={11}/>{exam.duration_minutes} {t('minutes')}
                                            </span>
                                        )}
                                        {exam.max_score > 0 && (
                                            <span className="text-xs text-indigo-200">• {exam.max_score} {t('points')}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-white text-indigo-600 rounded-lg text-xs font-bold shadow-sm">
                                    {t('Take exam')} →
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            {items.map((item, index) => {
                const {grade, score_type, score_number, score_1_number, score_2_number, score_text, final_score_number, final_score_text, final_grade_date} = item ?? {}

                const dateObj = final_grade_date ? dayjs(final_grade_date) : (grade && grade.grade_date ? dayjs(grade.grade_date) : null)
                const hasTime = dateObj && (dateObj.hour() !== 0 || dateObj.minute() !== 0)

                const displayScore = score_type === 'number' ? final_score_number : final_score_text
                const hasScore = displayScore !== null && displayScore !== undefined && displayScore !== ''

                const passedScore = score_type === 'number'
                    ? (score_2_number ? ((grade && grade.passed_score_2) || (grade && grade.passed_score))
                        : score_1_number ? ((grade && grade.passed_score_1) || (grade && grade.passed_score))
                        : (grade && grade.passed_score)) || null
                    : null
                const isPass = (passedScore != null && final_score_number != null)
                    ? final_score_number >= passedScore
                    : null

                const scoreColor = !hasScore ? 'text-color-400 bg-color-50 border-color-200'
                    : (score_type === 'number' && isPass === true) ? 'text-green-700 bg-green-50 border-green-300'
                    : (score_type === 'number' && isPass === false) ? 'text-red-600 bg-red-50 border-red-300'
                    : (score_type === 'number') ? 'text-color-200 bg-color-50 border-color-200'
                    : 'text-indigo-600 bg-indigo-50 border-indigo-300'

                const borderClass = !hasScore ? 'border-gray-200'
                    : (score_type === 'number' && isPass === true) ? 'border-green-500'
                    : (score_type === 'number' && isPass === false) ? 'border-red-400'
                    : 'border-gray-200'

                const hasActiveExam = grade && grade.course_class_exam && grade.course_class_exam.status === 1
                const examId = hasActiveExam && grade.course_class_exam.id
                const canTakeExam = hasActiveExam && isSelf && !hasScore

                const rowContent = (
                    <>
                        {dateObj && (
                            <div className="flex flex-col items-center justify-center rounded-lg border border-color-200 px-2 py-1.5 flex-shrink-0 bg-white shadow-sm" style={{minWidth: 48}}>
                                <span className="text-sm font-bold text-color-100 leading-none">{dateObj.format('DD')}</span>
                                <span className="text-[10px] text-color-400 leading-tight mt-0.5">{dateObj.format('MM/YYYY')}</span>
                                {hasTime && (
                                    <span className="text-[10px] text-primary font-medium leading-tight mt-0.5">{dateObj.format('HH:mm')}</span>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col space-y-1 flex-1 min-w-0">
                            <span className="font-semibold text-sm text-color-100 leading-tight truncate">{getTitle(grade)}</span>
                            <div className="flex items-center gap-1.5 text-xs text-color-400 leading-tight">
                                {grade && grade.subject && (
                                    <span className="font-medium text-color-300">{getTitle(grade.subject)}</span>
                                )}
                                {grade && grade.subject && grade.score_weight > 0 && <span>•</span>}
                                {grade && grade.score_weight > 0 && (
                                    <span className="italic">{t('score_weight')}: {grade.score_weight}</span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {canTakeExam ? (
                                <span className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold shadow-sm">
                                    <BsPencilSquare size={11}/>{t('Take exam')}
                                </span>
                            ) : (
                                <>
                                    <div className={classNames("text-xs font-bold px-2.5 py-1 rounded-full border", scoreColor)}>
                                        {hasScore ? (score_type === 'number' ? final_score_number : final_score_text) : t('undefined')}
                                    </div>
                                    {hasActiveExam && isSelf && hasScore && (
                                        <span className="text-2xs text-green-500 font-medium">{t('Graded')}</span>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )

                return canTakeExam ? (
                    <div
                        key={index}
                        role="button"
                        onClick={() => history.push('/course-class-exam/' + examId)}
                        className={classNames(
                            "flex items-center gap-3 p-3 border-l-4 rounded-r-xl bg-white cursor-pointer active:opacity-80 transition-opacity",
                            "border-indigo-500"
                        )}>
                        {rowContent}
                    </div>
                ) : (
                    <div key={index} className={classNames(
                        "flex items-center gap-3 p-3 border-l-4 rounded-r-xl bg-white",
                        borderClass
                    )}>
                        {rowContent}
                    </div>
                )
            })}
        </div>
    )
}

const AdminActiveExamsBanner = ({course_class_id, refreshKey}) => {
    const t = useTranslate()
    const [examGrades, setExamGrades] = useState([])

    const {handleAsyncAction: fetchExamGrades} = useAsyncAction({
        apiInfo: course_class_getGrades_Api,
        onSuccess: (result, {response}) => {
            const items = (response && response.data && response.data.data) || []
            setExamGrades(items.filter(g => g.course_class_exam && g.course_class_exam.status === 1))
        },
    })

    useEffect(() => {
        if (!course_class_id) return
        fetchExamGrades({}, {id: course_class_id})
    }, [course_class_id, refreshKey])

    if (!examGrades.length) return null

    return (
        <div className="flex flex-col gap-2 mb-1">
            {examGrades.map((grade, i) => {
                const exam = grade.course_class_exam
                return (
                    <Link
                        key={i}
                        to={getLinkToDetail(exam)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-sm hover:shadow-md transition-all no-underline">
                        <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-white/20">
                            <BsPencilSquare size={15}/>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-2xs text-indigo-200 font-semibold uppercase tracking-wide leading-none mb-0.5">
                                {t('exam available')}
                            </p>
                            <p className="font-bold text-sm leading-tight truncate">{getTitle(grade)}</p>
                            <div className="flex items-center gap-3 mt-1">
                                {exam.duration_minutes > 0 && (
                                    <span className="text-xs text-indigo-200">
                                        <BiTime size={10} className="inline mr-0.5"/>{exam.duration_minutes} {t('minutes')}
                                    </span>
                                )}
                                {exam.max_score > 0 && (
                                    <span className="text-xs text-indigo-200">• {exam.max_score} {t('points')}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition-colors">
                            {t('manage exam')} →
                        </div>
                    </Link>
                )
            })}
        </div>
    )
}

const CourseClassGradeTab = ({
                                 filter_actions = {
                                     sortBy: 'sort-by',
                                     keyword: 'keyword',
                                     course_id: 'course_id',
                                     subject_id: 'subject-id',
                                 },
                             }) => {
    const t = useTranslate()

    const {isSm} = useContext(LayoutContext)
    const [current, setCurrent] = useState('0')

    const {item} = useContext(CourseClassContext)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const can_edit = !!item?.edit
    const [viewAsRole, setViewAsRole] = useState(can_edit ? 'admin' : 'student')

    const course_class_id = getId(item)

    const owner_id = getId(item?.course)

    const owner_type = getType(item?.course)

    const classSubjects = useMemo(
        () => (item?.class_subjects ?? []).filter(cs => !!cs?.subject),
        [item?.class_subjects]
    )
    const subjects = useMemo(() => classSubjects.map(cs => cs.subject), [classSubjects])

    const [activeSubjectId, setActiveSubjectId] = useState(() => getId(subjects[0]) || null)

    const selectedCs = useMemo(
        () => classSubjects.find(cs => String(getId(cs.subject)) === String(activeSubjectId)),
        [classSubjects, activeSubjectId]
    )
    const passedScore = selectedCs?.passed_score ?? 0


    const {
        modal,
        refresh_token,
        handleAction = NullAsDefault,
    } = useCourseGrades({course_class: item})

    const refreshKey = useMemo(
        () => [refresh_token, course_class_id, activeSubjectId].join(''),
        [refresh_token, course_class_id, activeSubjectId]
    )

    const renderContextMenu = useCallback(
        (item) =>
            can_edit
                ? createContextMenu(
                [
                    // {
                    //     icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                    //     label: 'edit exam',
                    //     key: GradeActionTypes.COURSE_CLASS_EXAM_UPDATE,
                    //     invisible: !!!item?.course_class_exam
                    // },
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view grades',
                        key: GradeActionTypes.GRADE_DETAIL,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: GradeActionTypes.GRADE_UPDATE,
                    },
                    {
                        icon: <TbClockEdit size={14} className="text-color-400"/>,
                        label: 'history',
                        key: GradeActionTypes.GRADE_HISTORY,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: GradeActionTypes.GRADE_DELETE,
                    },
                ].filter((e) => item && !!!e?.invisible),
                (key) => {
                    handleAction(key, item)
                },
                {
                    icon: (
                        <div
                            style={{width: 22, height: 22, borderRadius: 999}}
                            className="flex flex-center background hover:background-200 text-color-500 hover:text-color-100 cursor-pointer">
                            <BsThreeDots size={12}/>
                        </div>
                    ),
                }
                )
                : undefined,
        [can_edit]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({subject_id: activeSubjectId})
            onSortChange(sortBy)
        },
        pagerInfo: {
            PaginationMeta: NullAsDefault,
            RenderEntity: React.Fragment,
            apiInfo: course_class_getGrades_Api,
            query: bindQueryParam({id: course_class_id}),
            renderItem: renderCourseClassGrade({
                renderContextMenu,
                handleAction,
            }),
            renderWrap: (child) => (
                <div className="flex flex-col gap-4">
                    {child}
                </div>
            )
        },
        renderSearchBar: NullAsDefault,
        SortByWidget: DateNameSortBy,
        query_params: filter_actions,
        sortHeader: t('list of grades'),
        withKey: createPagerKey(refreshKey),
        dependencies: [activeSubjectId],
        AdvancedFilter: CouseClassGradeFilter,
    })

    const subjectFilter = classSubjects.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
            {classSubjects.map((cs) => {
                const sid = getId(cs.subject)
                const isActive = String(activeSubjectId) === String(sid)
                const ps = cs.passed_score
                return (
                    <button
                        key={sid}
                        onClick={() => setActiveSubjectId(sid)}
                        className={classNames(
                            'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors select-none',
                            isActive
                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                : 'border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                        )}>
                        <span>{cs.subject?.title}</span>
                        {ps > 0 && (
                            <span className={classNames('text-2xs font-normal', isActive ? 'text-indigo-200' : 'text-color-400')}>
                                {`≥${ps}`}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )

    const adminTabs = [
        {key: '0', icon: <PiChartBarFill size={12}/>, label: t('grades')},
        {key: '1', icon: <PiUserListFill size={12}/>, label: t('students')},
    ]

    return (
        <div className="flex flex-col gap-3">
            {can_edit && (
                <CourseClassTabHeader
                    tabs={adminTabs}
                    activeKey={current}
                    onTabChange={setCurrent}
                    showTabs={viewAsRole === 'admin'}
                    role={viewAsRole}
                    onRoleChange={setViewAsRole}
                />
            )}

            {viewAsRole === 'admin' && can_edit ? (
                <>
                    {current === '0' && (
                        <div className="w-full flex flex-col flex-1 gap-3">
                            <AdminActiveExamsBanner
                                course_class_id={course_class_id}
                                refreshKey={refreshKey}
                            />
                            <div className="flex items-center justify-between gap-2 py-1">
                                <span className="text-xs text-color-400 italic">{t('create grades description')}</span>
                                <Button
                                    style={{padding: isSm ? "0 12px" : "0 18px"}}
                                    type="primary"
                                    className="button-rounded-md no-border flex flex-center font-medium text-xs md:text-sm"
                                    onClick={() => handleAction(GradeActionTypes.GRADE_CREATE)}>
                                    {t("create")}
                                </Button>
                            </div>
                            {subjectFilter}
                            {activeSubjectId
                                ? <div className="flex flex-col gap-3">{pager_widget}</div>
                                : <EmptyHolder title={t('please select a subject')}/>
                            }
                        </div>
                    )}
                    {current === '1' && (
                        <div className="w-full flex flex-col flex-1 gap-3">
                            {subjectFilter}
                            <GradeStudentReport
                                key={activeSubjectId}
                                course_class_id={course_class_id}
                                activeSubjectId={activeSubjectId}
                                passedScore={passedScore}/>
                        </div>
                    )}
                </>
            ) : (
                <div className="w-full flex flex-col flex-1 gap-3 py-1">
                    <MyBoard course_class_id={course_class_id}/>
                </div>
            )}

            {modal}
        </div>
    )
}

export default CourseClassGradeTab
